import * as React from "react";
import { Button } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import { cn } from "~/lib/utils";
import { Picture } from "~/components/ui/picture";
// Inlined (not <img>) so the wordmark's `var(--fill-0)` can take the heading color.
import driveLogo from "~/assets/products/drive-logo.svg?raw";
import viewLogo from "~/assets/products/view-logo.svg?raw";
import goLogo from "~/assets/products/go-logo.svg?raw";
import { imageUrl } from "~/lib/image-url";

export type ProductCardProduct = "drive" | "go" | "view";

const PRODUCT_HREF: Record<ProductCardProduct, string> = {
  drive: "/products/drive",
  go: "/products/go",
  view: "/products/view",
};

const LOGO: Record<ProductCardProduct, { svg: string; name: string }> = {
  drive: { svg: driveLogo, name: "Bouncie Drive" },
  go: { svg: goLogo, name: "Bouncie Go" },
  view: { svg: viewLogo, name: "Bouncie View" },
};

// Plain public/ paths so Picture can derive the .avif/.webp siblings.
// Position/size are percentages of the image panel at each breakpoint's own aspect
// ratio (aspect-[263/242] default/mobile, aspect-[229/242] sm/tablet, aspect-[302/279] lg),
// matching Figma's export crop at each size.
const HERO: Record<ProductCardProduct, { src: string; alt: string; className: string }> = {
  drive: {
    src: imageUrl("/images/products/home-drive-device.png"),
    alt: "Bouncie Drive GPS device",
    className:
      "left-[-11.84%] top-[13.37%] w-[123.95%] h-[87.87%] " +
      "sm:left-[-10.66%] sm:top-[21.71%] sm:w-[121.83%] sm:h-[75.21%] " +
      "lg:left-[-5.92%] lg:top-[21.01%] lg:w-[111.87%] lg:h-[78.99%]",
  },
  view: {
    src: imageUrl("/images/products/home-view-dashcam.png"),
    alt: "Bouncie View dash cam",
    className:
      "left-[-6.84%] top-[15.99%] w-[112.55%] h-[79.79%] " +
      "sm:left-[-4.8%] sm:top-[18.78%] sm:w-[108.3%] sm:h-[71.73%] " +
      "lg:left-[-3.07%] lg:top-[18.38%] lg:w-[104.97%] lg:h-[74.12%]",
  },
  go: {
    src: imageUrl("/images/products/nav-go.png"),
    alt: "Bouncie Go EV charger",
    className:
      "left-0 top-[7.04%] w-full h-[108.68%] " +
      "sm:left-[-7.58%] sm:top-[9.09%] sm:w-[115.33%] sm:h-[109.61%] " +
      "lg:left-[-1.24%] lg:top-[7.22%] lg:w-[102.32%] lg:h-[110.75%]",
  },
};

export interface ProductCardProps {
  product: ProductCardProduct;
  /** Category label shown under the wordmark. */
  subtitle: string;
  ctaLabel: string;
  className?: string;
  /** Set when the same cards render more than once on a page (the home page has one set per breakpoint): keeps each CTA's data-sid unique. */
  scope?: string;
}

function ProductCard({ product, subtitle, ctaLabel, className, scope }: ProductCardProps) {
  const sidScope = scope ? `${scope}:` : "";
  const hero = HERO[product];
  const logo = LOGO[product];
  const nameId = React.useId();
  const href = PRODUCT_HREF[product];

  return (
    <div
      className={cn(
        "relative flex w-[263px] shrink-0 flex-col overflow-hidden rounded-2xl sm:w-[229px] lg:w-[302px]",
        className,
      )}
    >
      <div className="bg-bg-alt border-border relative aspect-[263/242] shrink-0 overflow-hidden rounded-t-2xl border border-b-0 sm:aspect-[229/242] lg:aspect-[302/279]">
        <Picture src={hero.src} alt={hero.alt} className={cn("absolute max-w-none", hero.className)} />
      </div>

      <div className="border-border flex flex-1 flex-col gap-4 rounded-b-2xl border border-t-0 bg-white px-6 pt-4 pb-4 lg:pb-6">
        <div className="flex flex-col gap-2">
          <span
            id={nameId}
            role="img"
            aria-label={logo.name}
            className="text-text-heading block h-[22px] self-start lg:h-[34px] [&>svg]:h-full [&>svg]:w-auto"
            style={{ "--fill-0": "currentColor" } as React.CSSProperties}
            dangerouslySetInnerHTML={{ __html: logo.svg }}
          />
          <p className="text-text-body-alt text-sm leading-[20px] lg:text-base lg:leading-[1.5]">{subtitle}</p>
        </div>

        {/* mt-auto pins the CTA to the bottom when grid rows stretch cards to equal height */}
        <Button variant="primary" size="sm" className="mt-auto h-11 w-full text-base leading-5 tracking-[0.16px]" asChild>
          <Link data-sid={`product-card-cta:${sidScope}${href}`} to={href} aria-describedby={nameId}>
            {ctaLabel}
          </Link>
        </Button>
      </div>

      {/* Whole-card click target to the CTA's destination — aria-hidden/untabbable since the
          CTA link above already covers keyboard and screen-reader nav; last in DOM so it sits
          on top and catches clicks anywhere else on the card. */}
      <Link
        data-sid={`product-card-overlay:${sidScope}${href}`}
        to={href}
        aria-hidden
        tabIndex={-1}
        className="absolute inset-0 cursor-pointer"
      />
    </div>
  );
}

export { ProductCard };
